import React, { FormEvent, useState } from 'react'
import { useForm, Link, router } from '@inertiajs/react'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Alert from '@mui/material/Alert'
import IconButton from '@mui/material/IconButton'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'

interface RunnerData {
  id: number
  name: string
  slug: string
  status: string
  status_color: string
  metadata: Record<string, unknown>
  has_public_key: boolean
  approved_at: string | null
  approved_by: string | null
  revoked_at: string | null
  revoked_by: string | null
  last_heartbeat_at: string | null
  created_at: string
  updated_at: string
}

interface Assignment {
  id: number
  agent: { id: number; username: string; agent_type: string }
  assigned_at: string | null
  unassigned_at: string | null
  assigned_by: string | null
  active: boolean
  created_at: string
}

interface ClientEvent {
  id: number
  title: string
  description: string | null
  severity: string
  metadata: Record<string, unknown>
  eventable_type: string
  eventable_id: number
  created_at: string
}

interface AvailableAgent {
  id: number
  username: string
  agent_type: string
}

interface Props {
  runner: RunnerData
  assignments: Assignment[]
  events: ClientEvent[]
  available_agents: AvailableAgent[]
  registration_token: string | null
  registration_token_expires_at: string | null
  installer_url: string | null
}

function statusChipColor(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'approved': return 'success'
    case 'pending': return 'warning'
    case 'denied': return 'error'
    case 'revoked': return 'default'
    default: return 'default'
  }
}

export default function Edit({ runner, assignments, events, available_agents, registration_token, registration_token_expires_at, installer_url }: Props) {
  const { data, setData, patch, processing, errors } = useForm({
    runner: { name: runner.name },
  })
  const [selectedAgentId, setSelectedAgentId] = useState<number | ''>('')
  const [unassignTarget, setUnassignTarget] = useState<Assignment | null>(null)
  const [copied, setCopied] = useState(false)
  const [eventSeverityFilter, setEventSeverityFilter] = useState<string>('all')

  const installCommand = installer_url ? `curl -fsSL ${installer_url} | sudo bash` : null

  function handleCopyCommand() {
    if (installCommand) {
      navigator.clipboard.writeText(installCommand)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    patch(`/manage/runners/${runner.slug}`)
  }

  function handleAssignAgent() {
    if (selectedAgentId) {
      router.post(`/manage/runners/${runner.slug}/assign_agent`, { agent_id: selectedAgentId })
      setSelectedAgentId('')
    }
  }

  function handleUnassign() {
    if (unassignTarget) {
      router.delete(`/manage/runners/${runner.slug}/unassign_agent/${unassignTarget.id}`)
      setUnassignTarget(null)
    }
  }

  const activeAssignments = assignments.filter(a => a.active)
  const inactiveAssignments = assignments.filter(a => !a.active)
  return (
    <SidebarLayout section="manage" title={`Edit runner: ${runner.name}`}>
      {/* Runner details */}
      <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, maxWidth: 800, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Chip label={runner.status} color={statusChipColor(runner.status)} variant="outlined" />
          {runner.has_public_key && <Chip label="Has public key" size="small" variant="outlined" color="info" />}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Created</Typography>
            <Typography variant="body2">{new Date(runner.created_at).toLocaleString()}</Typography>
          </Box>
          {runner.approved_at && (
            <Box>
              <Typography variant="caption" color="text.secondary">Approved</Typography>
              <Typography variant="body2">{new Date(runner.approved_at).toLocaleString()} by {runner.approved_by}</Typography>
            </Box>
          )}
          {runner.revoked_at && (
            <Box>
              <Typography variant="caption" color="text.secondary">Revoked</Typography>
              <Typography variant="body2">{new Date(runner.revoked_at).toLocaleString()} by {runner.revoked_by}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary">Last heartbeat</Typography>
            <Typography variant="body2">{runner.last_heartbeat_at ? new Date(runner.last_heartbeat_at).toLocaleString() : 'Never'}</Typography>
          </Box>
        </Box>

        {/* Status actions */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          {runner.status === 'pending' && runner.has_public_key && (
            <>
              <Button variant="contained" color="success" size="small" onClick={() => router.post(`/manage/runners/${runner.slug}/approve`)}>
                Approve
              </Button>
              <Button variant="outlined" color="error" size="small" onClick={() => router.post(`/manage/runners/${runner.slug}/deny`)}>
                Deny
              </Button>
            </>
          )}
          {runner.status === 'pending' && !runner.has_public_key && (
            <Alert severity="warning" sx={{ py: 0.5, flexGrow: 1 }}>
              Waiting for runner to connect... Approve/Deny will be available once the runner registers.
            </Alert>
          )}
          {runner.status === 'approved' && (
            <Button variant="outlined" color="error" size="small" onClick={() => router.post(`/manage/runners/${runner.slug}/revoke`)}>
              Revoke
            </Button>
          )}
          <Button variant="outlined" size="small" onClick={() => router.post(`/manage/runners/${runner.slug}/generate_registration_token`)}>
            Generate registration token
          </Button>
        </Box>

        {/* Install command */}
        {installCommand && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={600}>Install command</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Run this on the target machine to install the agent runner:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  bgcolor: 'grey.100',
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  flexGrow: 1,
                  fontSize: '0.8rem',
                }}
              >
                {installCommand}
              </Typography>
              <IconButton size="small" onClick={handleCopyCommand} color={copied ? 'success' : 'default'}>
                {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Box>
            {registration_token_expires_at && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Expires: {new Date(registration_token_expires_at).toLocaleString()}
              </Typography>
            )}
          </Alert>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Edit name form */}
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Name"
            fullWidth
            value={data.runner.name}
            onChange={e => setData('runner', { ...data.runner, name: e.target.value })}
            error={'runner.name' in errors}
            helperText={(errors as Record<string, string>)['runner.name']}
            sx={{ mb: 2 }}
          />
          <Button type="submit" variant="contained" disabled={processing}>
            Update runner
          </Button>
        </Box>
      </Paper>

      {/* Agent assignments */}
      <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, maxWidth: 800, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Agent assignments</Typography>

        {/* Assign new agent */}
        {available_agents.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Assign agent</InputLabel>
              <Select
                value={selectedAgentId}
                label="Assign agent"
                onChange={e => setSelectedAgentId(e.target.value as number)}
              >
                {available_agents.map(agent => (
                  <MenuItem key={agent.id} value={agent.id}>{agent.username}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" size="small" onClick={handleAssignAgent} disabled={!selectedAgentId}>
              Assign
            </Button>
          </Box>
        )}

        {/* Active assignments */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell>Assigned by</TableCell>
                <TableCell>Assigned at</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeAssignments.map(a => (
                <TableRow key={a.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{a.agent.username}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{a.assigned_by || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {a.assigned_at ? new Date(a.assigned_at).toLocaleDateString() : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label="Active" size="small" color="success" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" color="error" onClick={() => setUnassignTarget(a)}>
                      Unassign
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {inactiveAssignments.slice(0, 5).map(a => (
                <TableRow key={a.id} sx={{ opacity: 0.5 }}>
                  <TableCell>
                    <Typography variant="body2">{a.agent.username}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{a.assigned_by || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {a.assigned_at ? new Date(a.assigned_at).toLocaleDateString() : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label="Inactive" size="small" variant="outlined" />
                  </TableCell>
                  <TableCell />
                </TableRow>
              ))}
              {activeAssignments.length === 0 && inactiveAssignments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No agents assigned to this runner.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Events */}
      <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, maxWidth: 800, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Events</Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={eventSeverityFilter}
              onChange={e => setEventSeverityFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Severity</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events
                .filter(e => eventSeverityFilter === 'all' || e.severity === eventSeverityFilter)
                .map(event => (
                <TableRow key={event.id}>
                  <TableCell>
                    <Chip
                      label={event.severity}
                      size="small"
                      color={event.severity === 'error' ? 'error' : 'info'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{event.title}</Typography>
                    {event.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {event.description.substring(0, 120)}{event.description.length > 120 ? '...' : ''}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={event.eventable_type === 'AgentRunner' ? 'Runner' : 'Agent'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(event.created_at).toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No events recorded.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Link href="/manage/runners" style={{ color: '#1a1a1a' }}>Back to runners</Link>
      </Box>

      <ConfirmDialog
        open={!!unassignTarget}
        title="Unassign agent"
        message={`Are you sure you want to unassign '${unassignTarget?.agent.username}' from this runner?`}
        onConfirm={handleUnassign}
        onCancel={() => setUnassignTarget(null)}
      />
    </SidebarLayout>
  )
}
